import { CircularProgress, Tooltip } from '@civilgrid/ui/components';
import { useParams } from '@tanstack/react-router';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { useAsyncRetry } from 'react-use';
import { useSnapshot } from 'valtio';
import {
  getDataRequest as apiGetDataRequest,
  getProject as apiGetProject,
  getSupportingFiles as apiGetSupportingFiles,
} from '~/api/civilsearch';
import { type BillingTier, billingTierLabels } from '~/common/dataRequestUtils';
import BackButton from '../../common/BackButton';
import CivilSearchLayout from '../../common/CivilSearchLayout';
import NavBar from '../../common/NavBar';
import NavButton from '../../common/NavButton';
import { deriveRequestScope, regionPositionLabel } from './requestScope';
import store, { type Project } from './store';
import Chat from './tabs/chat';
import InfoBanner from './tabs/common/InfoBanner';
import Evidence from './tabs/evidence';
// tabs
import General from './tabs/general';
import invoicing from './tabs/invoicing';
import ProjectInfo from './tabs/projectInfo';
import SupportingFiles from './tabs/supportingFiles';

const tabConfig: {
  [key: string]: { label: string; content: () => JSX.Element | null };
} = {
  general: {
    label: 'General',
    content: General,
  },
  'supporting-files': {
    label: 'Files uploaded by user',
    content: SupportingFiles,
  },
  chat: {
    label: 'Chat',
    content: Chat,
  },
  'project-info': {
    label: 'Project Info',
    content: ProjectInfo,
  },
  // updates: {
  //   label: 'Updates',
  //   content: Updates,
  // },
  evidence: {
    label: 'Evidence - Files from agency',
    content: Evidence,
  },
  invoicing: {
    label: 'Invoicing',
    content: invoicing,
  },
} as const;

export default function RequestDetails() {
  const { requestId, tab } = useParams({ strict: false }) as { requestId: string; tab: string };
  const { project } = useSnapshot(store) as typeof store;

  const dataRequest = useAsyncRetry(async () => {
    const req = await apiGetDataRequest(requestId);
    const project = await apiGetProject<Project>(req.project.id);
    store.setProject(project);
    return req;
  }, [requestId]);

  const filesData = useAsyncRetry(async () => {
    if (!project?.id) {
      return { hasFiles: false };
    }

    const files = await apiGetSupportingFiles(project.id);
    const hasFiles = files.length > 0;

    if (hasFiles) {
      const mostRecentDate = new Date(Math.max(...files.map((file) => new Date(file.createdAt).getTime())));

      const formattedDate = DateTime.fromJSDate(mostRecentDate).toFormat('MM/dd/yyyy HH:mm');

      return {
        hasFiles,
        formattedDate,
      };
    }

    return { hasFiles };
  }, [project?.id]);

  const content = useMemo(() => {
    let Comp: () => JSX.Element | null;
    if (tab && tabConfig[tab]) {
      Comp = tabConfig[tab].content;
    } else {
      Comp = tabConfig.general.content;
    }

    return <Comp />;
  }, [tab]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const navItems = useMemo(() => {
    return Object.keys(tabConfig).map((tabId) => {
      const config = tabConfig[tabId];

      const shouldHighlight = tabId === 'supporting-files' && filesData.value?.hasFiles;

      return (
        <Tooltip
          asChild
          key={`request-nav-item-${tabId}`}
          text={`Last Upload: ${filesData.value?.formattedDate}`}
          isDisabled={tabId !== 'supporting-files' || !filesData.value?.hasFiles}
        >
          <NavButton
            to={`/admin/civil-search/requests/${requestId}/${tabId}`}
            isActive={tabId === tab}
            highlight={shouldHighlight}
          >
            {config.label}
          </NavButton>
        </Tooltip>
      );
    });
  }, [requestId, filesData]);

  if (!project || dataRequest.loading) {
    return <CircularProgress indeterminate />;
  }

  if (dataRequest.error) {
    return <div>Error fetching request details.</div>;
  }

  const tier = dataRequest.value?.project.billingTier as BillingTier | undefined;
  const tierLabel = tier ? billingTierLabels[tier] : '';
  // CG-749: the title shows both the parent agency and the sub-region so it's
  // unmissable that this request covers only part of the project × agency area.
  // The sub-region reads by its internal name when it has one, else by region number.
  const scope = dataRequest.value ? deriveRequestScope(dataRequest.value, dataRequest.value.siblings) : null;
  const subGeometryName = dataRequest.value?.subGeometry?.name ?? null;
  let regionSuffix = '';
  if (scope) {
    regionSuffix = scope.isCatchAll
      ? ` (${regionPositionLabel(scope)} — remaining area)`
      : ` (${subGeometryName ?? regionPositionLabel(scope)})`;
  } else if (dataRequest.value?.agencySubGeometryId) {
    regionSuffix = ` (${subGeometryName ?? 'sub-region'})`;
  }
  const requestTitle = dataRequest.value
    ? `${dataRequest.value.project.name} (${dataRequest.value.project.projectId})${tierLabel ? ` [${tierLabel}]` : ''} / ${dataRequest.value.agency.agencyName}${regionSuffix} - ${dataRequest.value.agency.type} - ${dataRequest.value.type}`
    : 'Data Request Details';

  return (
    <CivilSearchLayout
      title={
        <div className="flex items-center space-x-2">
          <BackButton to="/requests">Back</BackButton>

          <span>{requestTitle}</span>
        </div>
      }
    >
      <div className="flex h-full w-full grow flex-col justify-between overflow-y-clip">
        <NavBar>{navItems}</NavBar>
        <InfoBanner project={project} />
        <div className="grow overflow-y-auto">{content}</div>
      </div>
    </CivilSearchLayout>
  );
}
